import type { pointInter, pointInterId } from '@/pages/home/utils/buriedPoint';
import type { INavStudioGameMenuItem } from '@/pages/home/types/home';
import { dynamicString } from '@home/utils/dynamicString';
import moment from 'moment';
import { getPathFromDashboard, matchPathWithDashboard } from '@/pages/dashboard/utils/utils';
import {
  STUDIO_DEVELOPMENT,
  STUDIO_HUB_PATH_HOME,
  STUDIO_HUB_ROUTE_ENTRIES,
  STUDIO_HUB_SEGMENTS,
  STUDIO_HUB_SHELL,
  STUDIO_OPERATION,
  STUDIO_TAB_LIST,
  DATABRAIN_LINK_PAGE_PATH,
  DATABRAIN_PAGE_TYPES,
} from './constant';
import type { StudioHubShellKind, TStudioTab } from './constant';
import { isHeaderHubDashboardPath } from './studioHubRegistry';

const STUDIO_HUB_SEGMENT_BY_NORMALIZED = new Map<string, StudioHubShellKind>(
  STUDIO_HUB_SEGMENTS.map(
    segment => [segment.toLowerCase(), segment] as [string, StudioHubShellKind],
  ),
);

const STUDIO_HUB_SEGMENT_URL_PATTERN = STUDIO_HUB_SEGMENTS.map(s =>
  s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
).join('|');

/** 从 pathname 解析工作台第二层外壳类型（与 {@link STUDIO_HUB_ROUTE_ENTRIES} 同步，默认 home） */
export function parseStudioHubShellKind(pathname: string): StudioHubShellKind {
  const m = pathname.match(
    new RegExp(`/studio/[^/]+/(${STUDIO_HUB_SEGMENT_URL_PATTERN})(?:/|$)`),
  );
  const seg = (m?.[1] || '').toLowerCase();
  return STUDIO_HUB_SEGMENT_BY_NORMALIZED.get(seg) ?? STUDIO_HUB_SHELL.HOME;
}

export function getStudioHubBasePath(segment: StudioHubShellKind): string {
  const row = STUDIO_HUB_ROUTE_ENTRIES.find(e => e.segment === segment);
  return row?.basePath ?? STUDIO_HUB_PATH_HOME;
}

/** `studio_dashboard_list[].path` 与常量 `STUDIO_HUB_PATH_*` 比较用（去首尾空白、尾斜杠；小写对齐） */
export function normalizeStudioDashboardPath(path?: string): string {
  return String(path ?? '').trim().replace(/\/+$/, '').toLowerCase();
}

/**
 * StudioHeader 顶栏枢纽卡片图标 URL：
 * 优先 {@link STUDIO_HUB_ROUTE_ENTRIES}.headerHubCardImage；否则 studio_info.element_url；再无则空字符串（无兜底图）。
 */
export function resolveStudioHeaderHubCardImage(
  hubMenuPath: string | undefined,
  studioElementUrl?: string,
): string {
  const norm = normalizeStudioDashboardPath(hubMenuPath);
  const entry = STUDIO_HUB_ROUTE_ENTRIES.find(
    e =>
      normalizeStudioDashboardPath(e.basePath) === norm && e.headerHub,
  );
  if (!entry) {
    return studioElementUrl ?? '';
  }
  const row = entry as { headerHubCardImage?: string; };
  if (row.headerHubCardImage) {
    return row.headerHubCardImage;
  }
  return studioElementUrl ?? '';
}

/**
 * 按工作台第二层根路径查找菜单配置（与 `StudioWrap`/URL 外壳同类语义）。
 */
export function findStudioHubMenuByPath<T extends { path?: string; }>(
  list: readonly T[] | undefined,
  hubRootPath: string,
): T | undefined {
  const needle = normalizeStudioDashboardPath(hubRootPath);
  return list?.find((item) => normalizeStudioDashboardPath(item?.path) === needle);
}

/**
 * `studio_dashboard_list` 中顶栏枢纽项（{@link STUDIO_HUB_ROUTE_ENTRIES} `headerHub`），按 sort 排序。
 */
export function listStudioHeaderHubMenuItems<
  T extends { path?: string; sort?: string; },
>(list: readonly T[] | undefined): T[] {
  if (!list?.length) {
    return [];
  }
  return list
    .filter((item) => isHeaderHubDashboardPath(item?.path))
    .sort((a, b) => Number(a.sort ?? 0) - Number(b.sort ?? 0));
}

// studio路径解析
export function parseStudioUrlInfo(str = '') {
  const [version, system, studioId, gameCode, ...arr] = str.trim().split('/');
  const [hubPath = '', ...restArr] = arr;
  const isHubPath = STUDIO_TAB_LIST.includes(hubPath as TStudioTab);
  const realPath = (isHubPath ? restArr : arr).length > 0 ? (isHubPath ? restArr : arr).join('/') : '';
  return {
    version,
    system,
    studioId,
    gameCode,
    hubPath: isHubPath ? hubPath : '',
    realPath
  };
}

export function getStudioTabFromSearch(search = ''): TStudioTab {
  const tab = new URLSearchParams(search).get('hub');
  if (tab === STUDIO_DEVELOPMENT || tab === 'researchHub') {
    return STUDIO_DEVELOPMENT;
  }
  return STUDIO_OPERATION;
}

export function getStudioTabFromPath(pathname = ''): TStudioTab {
  const { hubPath } = parseStudioUrlInfo(pathname);
  if (hubPath === STUDIO_DEVELOPMENT) {
    return STUDIO_DEVELOPMENT;
  }
  return STUDIO_OPERATION;
}

export function getStudioTabFromUrl(pathname = '', search = ''): TStudioTab {
  const { hubPath } = parseStudioUrlInfo(pathname);
  if (hubPath) {
    return getStudioTabFromPath(pathname);
  }
  return getStudioTabFromSearch(search);
}

export function buildStudioGamePath(
  studioId = '',
  gameCode = '',
  pagePath = '',
  tab: TStudioTab = STUDIO_OPERATION,
  hasHubTabs = false,
) {
  const gameCodePath = gameCode ? `/${gameCode}` : '';
  const hubPath = hasHubTabs ? `/${tab}` : '';
  return `/studio/${studioId}${gameCodePath}${hubPath}${pagePath ? `/${pagePath}` : ''}`;
}

export const getExtendInfoInLog = (pageLogMes: pointInter, gameid: string) => {
  const logMes: pointInterId = {
    ...(pageLogMes || {}),
    uid: gameid,
    uidType: 'unified_id',
  };
  return logMes;
};

// 时间格式化（Home / KOL / CommonRangePicker 等共用）
// 日粒度
export const formatDayTime = (value: [moment.Moment, moment.Moment]) => [
  value[0].startOf('d').format('YYYY-MM-DD HH:mm:ss'),
  value[1].endOf('d').format('YYYY-MM-DD HH:mm:ss'),
];
// 周粒度
export const formatWeekTime = (value: [moment.Moment, moment.Moment]) => [
  value[0].startOf('isoWeek')?.format('YYYY-MM-DD HH:mm:ss'),
  value[1].endOf('isoWeek').format('YYYY-MM-DD HH:mm:ss'),
];
// 月粒度
export const formatMonthTime = (value: [moment.Moment, moment.Moment]) => [
  value[0].startOf('month').format('YYYY-MM-DD HH:mm:ss'),
  value[1].endOf('month').format('YYYY-MM-DD HH:mm:ss'),
];

/** 月区间：起止按自然月边界，与接口 start_time / end_time 对齐 */
export const formatMonthRangeTime = (value: [moment.Moment, moment.Moment]) => [
  value[0].clone().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
  value[1].clone().endOf('month').format('YYYY-MM-DD HH:mm:ss'),
];

/**
 * 按优先级计算 studio 入口跳转路径，供以下两类场景共用：
 *   - 顶部导航 / Studio Menu 点击进入 studio（NavHeader）
 *   - hub 路径（如 /pr、/ai-report）未在 studio_dashboard_list 中配置时的重定向（StudioWrap）
 *
 * 优先级：
 * 0. haveHome 为 true 时直接跳转 /studio/{id}/home
 * 1. studio_dashboard_list 第一个有效 path
 * 2. game_list 第一个游戏的 dashboard_list 第一个有效菜单路径
 *    （含 display_mode=tab：须用 getStudioMenuPath 生成 {nameSlug}/{firstChildPath}）
 * 3. 兜底 /home（全局首页；早期版本曾兜底 /studio/{id}/home，但当 home 也未配置时会引发死循环）
 */
export function resolveRedirectPathForMissingHub(
  studioData: {
    haveHome?: boolean;
    studio_dashboard_list?: readonly { path?: string; page_type?: string; url?: string; }[];
    game_list?: readonly {
      game_code?: string;
      unified_edition_id?: string;
      dashboard_list?: readonly INavStudioGameMenuItem[];
      dashboard_research_list?: readonly INavStudioGameMenuItem[];
    }[];
  } | undefined,
  studioId: string,
): string | null {
  if (!studioData) return null;

  // 0. 有首页时优先跳转 studio home
  if (studioData.haveHome) {
    return `/studio/${studioId}/home`;
  }

  const { studio_dashboard_list: dashboardList, game_list: gameList } = studioData;

  // 1. 优先取 studio_dashboard_list 第一个有效 path
  if (dashboardList?.length) {
    for (const item of dashboardList) {
      if (item?.path && item.page_type !== 'out_url' && item.page_type !== 'outPath') {
        return `/studio/${studioId}${item.path}`;
      }
    }
  }

  // 2. 取 game_list 第一个游戏的菜单：优先运营专区，否则研发专区
  //    入口可不带 /operation|development，GameWrap 会按默认专区展示
  if (gameList?.length) {
    const firstGame = gameList[0];
    const firstGameCode = firstGame?.game_code || firstGame?.unified_edition_id;
    const operationDashboards = firstGame?.dashboard_list || [];
    const researchDashboards = firstGame?.dashboard_research_list || [];
    const gameDashboards = operationDashboards.length
      ? operationDashboards
      : researchDashboards;

    if (firstGameCode && gameDashboards.length) {
      for (const dash of gameDashboards) {
        if (dash?.page_type === 'out_url' || dash?.page_type === 'outPath') {
          continue;
        }
        // tab 容器可能无 path，须用 getStudioMenuPath（与左侧菜单/GameWrap 一致）
        const dashboardPath = getStudioMenuPath(dash);
        if (dashboardPath) {
          return `/studio/${studioId}/${firstGameCode}/${dashboardPath}`;
        }
      }
    }
  }

  // 3. 兜底：跳转全局首页
  return '/home';
}

// 根据对象映射值
export const mapObjectValue = (
  data: Record<string, any> = {},
  orgData: Record<string, any> = {},
) => {
  const obj: Record<string, any> = {};
  Object.keys(data)?.forEach(key => {
    const value = data[key];
    obj[key] = typeof value === 'string' ? dynamicString(value, orgData) : value;
  });
  return obj;
};

// ─── display_mode=tab 菜单容器路由工具函数 ───────────────────────────────────

/**
 * 将 tab 容器 name_en 转为 URL slug
 * 例：'Multi Tab' → 'multi-tab'，'MultiTab' → 'multitab'
 */
export function toTabNameSlug(nameEn: string): string {
  return (nameEn || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

/**
 * Databrain Page 路由工具所需的最小字段集。
 * 菜单数据在不同调用方分别为 INavStudioGameMenuItem 与 IDashboardItem，
 * 两者字段集不完全一致，此处按结构取交集，避免调用点断言。
 */
type TDatabrainPageLike = {
  page_type?: string;
  name_en?: string;
  name_cn?: string;
};

/** 是否为 Databrain Page（iframe 嵌入的外部 Databrain 页面） */
export function isDatabrainPage(dashboard: TDatabrainPageLike | null | undefined): boolean {
  return DATABRAIN_PAGE_TYPES.includes(dashboard?.page_type ?? '');
}

/**
 * 由菜单名生成 URL slug，优先取 name_en，缺失时回退 name_cn。
 * - 先做 NFKC 归一，避免全角字符与半角生成不同 slug
 * - 保留中文（\u4e00-\u9fff）：name_cn 回退时不至于被清成空串
 * - 名称全部为非法字符或为空时兜底 'page'，保证 slug 恒非空
 * 例：'Multi Tab' → 'multi-tab'，'PR / 舆情' → 'pr-舆情'
 */
const getDatabrainPageSlug = (
  dashboard: TDatabrainPageLike | null | undefined,
): string => {
  const pageName = String(
    dashboard?.name_en || dashboard?.name_cn || 'page',
  ).normalize('NFKC');
  return pageName
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\u4e00-\u9fff-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'page';
};

/** URL 上用于定位具体 Databrain Page 的路由 key（由 name_en slug 生成） */
export function getDatabrainPageRouteKey(
  dashboard: TDatabrainPageLike | null | undefined,
): string {
  return getDatabrainPageSlug(dashboard);
}

/**
 * 判断 URL 上的 routeKey 是否指向该 Databrain Page。
 * 路由 key 仅由 name_en 生成（与 u_id 无关），要求同一列表内 name_en 唯一。
 */
export function matchDatabrainPageRouteKey(
  dashboard: TDatabrainPageLike | null | undefined,
  routeKey = '',
): boolean {
  let decodedRouteKey = routeKey;
  try {
    decodedRouteKey = decodeURIComponent(routeKey);
  } catch (e) {
    // 非法编码的 URL 保留原值比较
  }

  return decodedRouteKey === getDatabrainPageRouteKey(dashboard);
}

/** Databrain Page 的路由路径（非 / 开头），形如 `databrainLink/{routeKey}` */
export function getDatabrainPagePath(
  dashboard: TDatabrainPageLike | null | undefined,
): string {
  const routeKey = getDatabrainPageRouteKey(dashboard);
  return routeKey
    ? `${DATABRAIN_LINK_PAGE_PATH}/${encodeURIComponent(routeKey)}`
    : DATABRAIN_LINK_PAGE_PATH;
}

/**
 * 获取 studio 菜单项的路由路径（非 / 开头）
 * - 普通菜单：直接调用 getPathFromDashboard
 * - display_mode=tab 容器：返回 {nameSlug}/{firstChildPath}
 *   例：multitab/opinionindex
 *
 * 不修改 dashboard 系统的 getPathFromDashboard，studio 侧统一用此函数
 */
export function getStudioMenuPath(dashboard: INavStudioGameMenuItem | null | undefined): string {
  if (!dashboard) return '';
  if (dashboard.display_mode === 'tab' && dashboard.tabs?.length) {
    const firstTab = dashboard.tabs[0];
    return buildTabChildPath(dashboard.name_en, firstTab);
  }
  if (isDatabrainPage(dashboard)) {
    return getDatabrainPagePath(dashboard);
  }
  return getPathFromDashboard(dashboard);
}

/**
 * 生成 tab 子页路径（非 / 开头）
 * 格式：{nameSlug}/{childRoutePath}
 * 例：multitab/opinions（page_type=path）
 *     multitab/authPath/report/Auth-Path（page_type=auth_path）
 *
 * 与左侧菜单逻辑完全一致：通过 getPathFromDashboard 获取路由路径
 */
export function buildTabChildPath(nameEn: string, tabItem: any): string {
  const slug = toTabNameSlug(nameEn);
  const childRoutePath = isDatabrainPage(tabItem)
    ? getDatabrainPagePath(tabItem)
    : getPathFromDashboard(tabItem);
  return `${slug}/${childRoutePath}`;
}

/**
 * 从 tab 容器完整路径中剥离容器段，取回子页路径。
 * realPath 格式为 {nameSlug}/{childPath}，仅按首个 '/' 切分——
 * childPath 自身可能含 '/'（如 databrainLink/{routeKey}），需整体保留。
 * 无 '/' 时表示只有容器段、未指向具体子页，返回空串。
 */
export function getTabChildPathFromRealPath(realPath: string): string {
  const slashIdx = realPath.indexOf('/');
  return slashIdx >= 0 ? realPath.slice(slashIdx + 1) : '';
}

/**
 * 判断左侧菜单项是否与当前路径匹配（用于菜单高亮）。
 * - display_mode=tab 的容器不在此判定，由 isTabContainerActive 负责
 * - Databrain Page 走 routeKey 比对；裸 databrainLink 路径缺少 routeKey，
 *   无法定位具体页面，对所有 Databrain Page 均返回 true（由调用方取首个命中）
 * - 其余菜单沿用 dashboard 侧的 matchPathWithDashboard
 */
export function matchStudioMenuPath(
  dashboard: INavStudioGameMenuItem | null | undefined,
  realPath: string,
): boolean {
  if (!dashboard || dashboard.display_mode === 'tab') return false;
  if (isDatabrainPage(dashboard)) {
    if (realPath === DATABRAIN_LINK_PAGE_PATH) {
      return true;
    }
    const routePrefix = `${DATABRAIN_LINK_PAGE_PATH}/`;
    return realPath.startsWith(routePrefix)
      && matchDatabrainPageRouteKey(dashboard, realPath.slice(routePrefix.length));
  }
  return matchPathWithDashboard(dashboard, realPath);
}

/**
 * 判断 tab 容器下的 childPath 是否指向该子 tab。
 * Databrain Page 子 tab 用 name_en slug 区分；裸 databrainLink 路径缺少 routeKey，
 * 无法定位具体页面，命中首个 Databrain 子 tab。
 */
export function matchStudioTabChildPath(
  tabItem: INavStudioGameMenuItem,
  childPath: string,
): boolean {
  if (isDatabrainPage(tabItem)) {
    const routePrefix = `${DATABRAIN_LINK_PAGE_PATH}/`;
    if (childPath.startsWith(routePrefix)) {
      return matchDatabrainPageRouteKey(tabItem, childPath.slice(routePrefix.length));
    }
    return childPath === DATABRAIN_LINK_PAGE_PATH;
  }
  const tabRoutePath = getPathFromDashboard(tabItem);
  return tabRoutePath === childPath || childPath.startsWith(`${tabRoutePath}/`);
}

/**
 * 判断 realPath 是否属于某个 tab 容器
 * realPath 格式：{nameSlug}/{childPath}
 * 通过 pageMenu（第一段）与 nameSlug 匹配
 */
export function isTabContainerActive(nameEn: string, realPath: string): boolean {
  const slug = toTabNameSlug(nameEn);
  return realPath === slug || realPath.startsWith(`${slug}/`);
}

/**
 * 从 tab 容器路径中提取原始子页路径（用于跨游戏路径保持匹配）
 * {nameSlug}/{childPath} → {childPath}
 */
export function extractTabChildRealPath(prefixedPath: string): string {
  const slashIdx = prefixedPath.indexOf('/');
  if (slashIdx < 0) return '';
  return prefixedPath.slice(slashIdx + 1);
}
